"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  TrendingUp,
  Sparkles,
  Target,
  Activity,
  Eye,
  Megaphone,
  Shield,
  ArrowRight,
  Crown,
  Mic,
  Layers,
  Flame,
  Globe2,
  CheckCircle2,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { BentoBlock } from "@/lib/blocks/bento-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const EVIDENCE = [
  { metric: "95-98", suffix: "%", label: "Organic Authenticity", sub: "Statistically indistinguishable", accent: "lime" },
  { metric: "300-500", suffix: "%", label: "Penetration Improvement", sub: "Over baseline organic reach", accent: "emerald" },
  { metric: "1000", suffix: "+", label: "Distribution Points", sub: "Multi-platform syndication", accent: "cyan" },
  { metric: "50", suffix: "+", label: "Platforms", sub: "Native deployment", accent: "purple" },
];

const DIMENSIONS = [
  { num: "01", title: "Reality Framework Construction", body: "Understanding existing belief structures, cultural contexts, and cognitive frameworks. Constructing perception architectures around authentic strengths." },
  { num: "02", title: "Narrative Trajectory Design", body: "Engineering the trajectory of perception over time through sequential narrative deployment. Projecting where perception will be in three, six, and twelve months." },
  { num: "03", title: "Perception Anchor Establishment", body: "Creating stable reference points against attack. Irrefutable truths, verified achievements, authenticated endorsements. The anchors hold when narratives are challenged." },
  { num: "04", title: "Multi-Dimensional Consistency", body: "Maintaining consistent brand perception across every platform, every piece of content, every interaction. Inconsistency is the vulnerability attackers exploit." },
  { num: "05", title: "Reinforcement Loops", body: "Building systems where positive perception generates more positive perception. Credibility begets trust, trust begets influence, influence begets authority." },
];

const STAGES = [
  { num: "01", title: "Intelligence", body: "Every engagement begins with intelligence gathering. The ground truth is discovered before any strategy is formed.", accent: "red" },
  { num: "02", title: "Analysis", body: "Pattern identification, anomaly detection, correlation mapping. The analysis is forensic, not descriptive. The symptom is never the disease.", accent: "purple" },
  { num: "03", title: "Strategy", body: "The Penta P framework is deployed. Each pillar assigned its role. Strategy is an operational plan driving coordinated action.", accent: "cyan" },
  { num: "04", title: "Orchestration", body: "All platforms coordinated simultaneously through the neural command interface. Decision time compressed from days to under one hour.", accent: "emerald" },
  { num: "05", title: "Amplification", body: "Content deploys at scale with platform-specific optimization. The signal becomes louder than the noise. Reach, resonance, results.", accent: "amber-gold" },
  { num: "06", title: "Feedback", body: "Continuous monitoring and outcome measurement flow back into intelligence. The loop never closes. It accelerates.", accent: "denim" },
];

const PIPELINE = [
  { num: "01", title: "Identification", body: "Content requirements defined across type, characteristic, and channel. Volume benchmarks calibrated per engagement.", accent: "red" },
  { num: "02", title: "Categorization", body: "Content targeted to specific audience segments across five dimensions: geographical, behavioral, cultural, social, and sentimental.", accent: "purple" },
  { num: "03", title: "Curation", body: "Ten curation domains transform raw intelligence into content opportunities. Authentic narratives, organic endorsements, cultural currents.", accent: "cyan" },
  { num: "04", title: "Creation", body: "Five creation modes: promotion, neutralization, demotion, transformation, assertion. Multi-format production across all channels.", accent: "emerald" },
  { num: "05", title: "Auditing", body: "Five-dimensional quality assurance: logical, grammatical, legal, emotional, historical. Every piece passes before deployment.", accent: "amber-gold" },
];

const MODES = [
  { title: "Sustained Operations", body: "Organic, stable, continuous content deployment. The foundation layer. Consistent, compliant, and compounding. Build authority over time through legitimate means." },
  { title: "Flare Operations", body: "Aggressive viral campaigns, trend manipulation, buzz creation, high-velocity deployment. Used when speed and shock are more important than sustainability." },
  { title: "Proliferation Operations", body: "The combined approach integrating amplification, review management, crisis management, dialogue control, sentiment management, and narrative propagation across both white-hat and black-hat techniques." },
];

export default function PerceptionReputationPage() {
  return (
    <>
      <PageHero
        icon={TrendingUp}
        overline="Perception & Reputation"
        overlineClassName="text-lime"
        title={
          <>
            Engineering How the <span className="text-gradient-premium">World Sees You.</span>
          </>
        }
        subtitle="Public perception is not an opinion. It is an operating environment. The perception management practice Dr. Jyoti Kush oversees architects, proliferates, and defends reputation at sovereign scale — deploying narratives with ninety-five to ninety-eight percent organic authenticity across hundreds of platforms, dozens of languages, and thousands of distribution points."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Perception & Reputation" }]}
        badge="Narrative engineering at sovereign scale"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-5`}>
              <Sparkles className="size-3.5 text-lime" />
              <span className="text-overline text-lime">The Perception Imperative</span>
            </div>
            <h2 className="text-display-md mb-5">
              In the digital age, perception is not a derivative of reality. It is reality.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              How an entity is perceived — across search results, social media, news coverage, reviews, forums, comments, blogs, and every digital surface — determines whether that entity wins or loses. Whether a government maintains public trust. Whether a corporation retains market position. Whether a royal family preserves generational legacy.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Perception management at sovereign scale is not reactive. It is architectural. The narrative is designed before it is deployed. The defense is built before the attack. The intelligence is gathered before the strategy is formed. This is the perception imperative.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Target className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Five-Dimensional Framework</span>
          </div>
          <h2 className="text-display-md mb-4">Engineering perception across five dimensions.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each dimension reinforces the others, creating a perception structure resilient to attack, aggressive in its reach, and adaptive to shifting sentiment landscapes.
          </p>
        </div>
        <div className="space-y-3">
          {DIMENSIONS.map((d, i) => {
            const a = ACCENT[["red", "purple", "cyan", "emerald", "amber-gold"][i]];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className={`size-14 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg`}>
                    <span className="text-body-md font-bold text-white">D{`0${i + 1}`}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-1 ${a.text}`}>{d.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{d.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Activity className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Six-Stage Process</span>
          </div>
          <h2 className="text-display-md mb-4">How perception engagements operate.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every perception engagement follows this battle-tested methodology refined over hundreds of sovereign-scale operations.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {STAGES.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{s.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{s.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Megaphone className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Content Creation Pipeline</span>
          </div>
          <h2 className="text-display-md mb-4">Five stages of engineering for perception management.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every piece of content passes through this engineering pipeline — from identification through auditing — ensuring sovereign-grade quality standards.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
          {PIPELINE.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className={`text-caption font-bold ${a.text} opacity-50 mb-2`}>Stage {s.num}</div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{s.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Globe2 className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Three Operational Modes</span>
          </div>
          <h2 className="text-display-md mb-4">Calibrating intensity to stakes.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The Propulsion Framework governs the intensity, velocity, and strategic posture of every perception engagement.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {MODES.map((m, i) => {
            const accents = ["emerald", "amber-gold", "purple"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-3 ${a.text}`}>{m.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{m.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="perception-metrics" ariaLabel="Perception Metrics">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Eye className="size-3.5 text-lime" />
            <span className="text-overline text-lime">The Perception Metrics</span>
          </div>
          <h2 className="text-display-md mb-4">Six metrics. One signature measurement framework.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature compilation. Each metric is measured continuously, not periodically. The framework is the difference between reactive reputation management and engineered perception architecture.
          </p>
        </div>
        <CounterBlock
          columns={4}
          stats={[
            { value: 95, suffix: "-98", label: "Organic Authenticity %", description: "Content statistically indistinguishable from organic spread", accent: "lime" },
            { value: 300, suffix: "-500", prefix: "", label: "Penetration Improvement %", description: "Over baseline organic reach", accent: "emerald" },
            { value: 1000, suffix: "+", label: "Distribution Points", description: "Multi-platform syndication", accent: "cyan" },
            { value: 50, suffix: "+", label: "Platforms", description: "Native deployment channels", accent: "purple" },
            { value: 50000, suffix: "+", label: "Content Variants/Hour", description: "Through four core engines", accent: "amber-gold" },
            { value: 144, label: "Perception Dimensions", description: "Real-time measurement of sentiment, opinion, buzz, hearsay, dialogue, favorability", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Megaphone className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Dissemination Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">Official and unofficial channels. Coordinated.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The dissemination architecture operates on two rails — official channels for verifiable statements, unofficial channels for narrative propagation. The two rails are coordinated through the neural command interface.
          </p>
        </div>
        <BentoBlock
          items={[
            { title: "Official Channels", body: "Press releases, media briefings, official social media accounts, executive communications. The verified voice. The channel through which formal positions are stated.", icon: <Megaphone className="size-5" />, accent: "red", span: "lg" },
            { title: "Verified Distribution", body: "One thousand-plus syndication points, including wire services, premium media placements, and partner networks. The amplifier for the verified voice.", icon: <Activity className="size-5" />, accent: "purple", span: "sm" },
            { title: "Unofficial Channels", body: "Influencer networks, community platforms, organic-seeding programs, third-party endorsers. The channel through which narrative propagates as discovered content.", icon: <Eye className="size-5" />, accent: "cyan", span: "sm" },
            { title: "Influencer Defense Network", body: "Eight hundred fifty thousand-plus profiled advocates. The network that amplifies the verified voice through authentic channels. Ready for rapid activation.", icon: <Globe2 className="size-5" />, accent: "emerald", span: "sm" },
            { title: "Content Engines", body: "Four core engines producing one thousand to one hundred thousand-plus content variants per campaign. Platform-specific optimization. Native voice in every channel.", icon: <Layers className="size-5" />, accent: "amber-gold", span: "sm" },
            { title: "Search and SEO Control", body: "Search dominance through content volume, link authority, and platform-specific optimization. The channels through which adversarial narratives can be demoted.", icon: <Target className="size-5" />, accent: "denim", span: "wide" },
            { title: "Demotion Arsenal", body: "Algorithmic demotion of adversarial content. Search result optimization. Negative SEO counter-measures. The toolkit that suppresses, not amplifies.", icon: <Flame className="size-5" />, accent: "coral", span: "sm" },
            { title: "Multi-Language Operations", body: "Fifty-plus languages. Cultural calibration embedded in every narrative. The capacity to maintain perception architecture across linguistic and cultural boundaries.", icon: <BookOpen className="size-5" />, accent: "lime", span: "sm" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Five Creation Modes</span>
          </div>
          <h2 className="text-display-md mb-4">Five strategic postures. One unified framework.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every perception engagement operates across five creation modes. The mode selected — and the intensity at which it operates — is calibrated to the diagnostic, the audience, and the strategic objective.
          </p>
        </div>
        <PillarGridBlock
          columns={5}
          variant="card"
          pillars={[
            { title: "Promotion", body: "Active amplification of favorable content. The standard operating mode. Builds authority and reach through legitimate, measurable, sustainable means.", icon: <TrendingUp className="size-5" />, accent: "red" },
            { title: "Neutralization", body: "Adversarial content diluted, contested, or supplanted. Counter-narratives deployed with surgical precision. The mode that defends without escalating.", icon: <Shield className="size-5" />, accent: "purple" },
            { title: "Demotion", body: "Search and algorithmic suppression of harmful content. The mode that uses the same platforms the adversary uses — but with greater sophistication.", icon: <Flame className="size-5" />, accent: "cyan" },
            { title: "Transformation", body: "Crisis converted into narrative reinforcement. The mode that turns the adversary's attack into the subject's platform.", icon: <Sparkles className="size-5" />, accent: "emerald" },
            { title: "Assertion", body: "Direct, verifiable, documented truth-telling. The mode that establishes the perceptual anchor that survives every assault.", icon: <CheckCircle2 className="size-5" />, accent: "amber-gold" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-lime/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-lime mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage the perception architecture.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Sovereign-grade narrative engineering for governments, corporations, royal families, and public figures. Every engagement begins with intelligence. Every outcome verified.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: TrendingUp, accent: "lime" },
          { title: "Crisis Management", description: "The crisis transformation engine. Compressing damage windows and recovering in 30-60 days.", href: "/expertise/crisis-management/", icon: Flame, accent: "amber-gold" },
          { title: "Political Strategy", description: "Engineering electoral outcomes. Booth-level intelligence and verified campaign victories.", href: "/expertise/political-strategy/", icon: Target, accent: "cyan" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "purple" },
        ]}
      />
    </>
  );
}
